import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    IconButton,
    Button,
    Box,
    Divider,
    useTheme,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const DetailRow = ({ label, value }) => (
    <Grid container spacing={2} sx={{ py: 1.5, borderBottom: '1px dashed rgba(0,0,0,0.1)' }}>
        <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
            <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                {value || 'N/A'}
            </Typography>
        </Grid>
    </Grid>
);

DetailRow.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node])
};

const PVDetailsModal = ({ open, onClose, data }) => {
    const theme = useTheme();

    if (!data) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: '#fff' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h4" component="div" sx={{ color: '#fff' }}>
                        Agent Verification Details
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.grey[100]
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
                <Grid container spacing={4}>
                    {/* Left Column - Details */}
                    <Grid item xs={12} md={7}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h5" color="secondary" gutterBottom>
                                Basic Information
                            </Typography>
                            <DetailRow label="Agent Name" value={data.bcname} />
                            <DetailRow label="Agent Code" value={data.agent_code} />
                            <DetailRow
                                label="Status"
                                value={<Chip label={data.status} size="small" color={data.status === 'SUCCESS' ? 'success' : 'default'} />}
                            />
                            <DetailRow label="Shop Name" value={data.shop_name} />
                            <DetailRow label="Address" value={data.address} />
                            <DetailRow label="Place/City" value={`${data.place} / ${data.city}`} />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h5" color="secondary" gutterBottom>
                                Verification Status
                            </Typography>
                            <DetailRow
                                label="VKYC Status"
                                value={
                                    <Chip
                                        label={data.vkyc_status}
                                        size="small"
                                        color={data.vkyc_status === 'Verified' ? 'success' : 'warning'}
                                        variant="outlined"
                                    />
                                }
                            />
                            <DetailRow label="VKYC Date" value={data.vkycDate} />
                            <DetailRow label="PCC Verification" value={data.pcc_verification_status} />
                            <DetailRow label="TAT Status" value={data.sla_status} />
                        </Box>
                    </Grid>

                    {/* Right Column - Video & Allocation */}
                    <Grid item xs={12} md={5}>
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                height: '100%'
                            }}
                        >
                            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PlayCircleOutlineIcon color="primary" /> VKYC Recording
                            </Typography>

                            {data.vkycDocument && data.vkycDocument !== 'N/A' ? (
                                <Box
                                    sx={{
                                        mt: 2,
                                        width: '100%',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        bgcolor: '#000',
                                        aspectRatio: '16/9',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                    <video src={data.vkycDocument} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }}>
                                        Your browser does not support the video tag.
                                    </video>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        mt: 2,
                                        width: '100%',
                                        height: 200,
                                        bgcolor: 'grey.100',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        color: 'text.secondary',
                                        border: '2px dashed',
                                        borderColor: 'grey.300'
                                    }}
                                >
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.1, 1],
                                            opacity: [0.5, 0.8, 0.5]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: 'easeInOut'
                                        }}
                                    >
                                        <PlayCircleOutlineIcon sx={{ fontSize: 60, color: 'grey.400' }} />
                                    </motion.div>
                                    <Typography variant="body2" sx={{ mt: 2, fontWeight: 500, color: 'grey.500' }}>
                                        Video Not Available
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                                        VKYC recording not found
                                    </Typography>
                                </Box>
                            )}

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h5" gutterBottom>
                                Allocation Info
                            </Typography>
                            <DetailRow label="Allocated RO" value={data.pcc_allocated_ro} />
                            <DetailRow label="Allocated Employee" value={data.pcc_allocated_employee} />
                            <DetailRow label="Created At" value={data.created_at} />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="contained" color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

PVDetailsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    data: PropTypes.object
};

export default PVDetailsModal;
